import { useEffect, useRef } from "react";
import { calculationPersistFingerprint } from "../utils/calculation-persist-fingerprint";
import { registerOfferSave } from "../utils/offer-save-queue";

/**
 * Sync nested calculation Formik values back to the parent.
 * - Immediately marks dirty + mirrors values into parent state (so
 *   enableReinitialize / soft-sync / getOffer cannot wipe offer_group_id
 *   during the debounce window after DnD / «Flytt til gruppe»).
 * - Debounces the actual API persist (one PUT per pause in typing).
 * - Flushes a pending persist on unmount (tab switch).
 * - `suppressEpochRef` (parent-owned, bumped synchronously on placement PUT)
 *   drops stale debounced commits that were scheduled before the move.
 *   Reading the parent ref avoids the React effect lag where a timeout could
 *   fire after setState(suppressEpoch) but before useEffect synced a local ref.
 * - Dirty detection uses calculationPersistFingerprint (not full values JSON):
 *   PUT responses always change updated_at / summary / totals and would
 *   otherwise re-trigger Autosave forever after enableReinitialize.
 * - `settleEpoch` (bumped after successful PUT) absorbs the post-save Formik
 *   merge + orderItems heal without scheduling another PUT.
 */
export default function CalculationAutosave({
  offerId,
  values,
  selectedIndex,
  onCommit,
  onLocalSync,
  onSaveState = null,
  suppressEpoch = 0,
  suppressEpochRef,
  settleEpoch = 0,
  settleFingerprintRef,
  /** Unresolved conflict: keep mounted but discard pending — never flush deletes. */
  paused = false,
  /** Parent discard generation; Use Server bumps so stale flushes abort. */
  discardGenerationRef,
}) {
  const timeoutRef = useRef(null);
  const lastHashRef = useRef(null);
  const lastSentHashRef = useRef(null);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const isInitialRef = useRef(true);
  const pendingRef = useRef(null);
  const inFlightRef = useRef(null);
  const flushRef = useRef(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const onSaveStateRef = useRef(onSaveState);
  onSaveStateRef.current = onSaveState;

  const readSuppressEpoch = () =>
    suppressEpochRef?.current ?? suppressEpoch ?? 0;

  const readDiscardGeneration = () => discardGenerationRef?.current ?? 0;

  const discardPending = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingRef.current = null;
  };

  flushRef.current = async () => {
    if (inFlightRef.current) await inFlightRef.current;
    clearTimeout(timeoutRef.current);
    const pending = pendingRef.current;
    if (!pending) return;
    // Conflict pause / Use Server suppress / write-generation bump: drop draft.
    if (pausedRef.current) {
      pendingRef.current = null;
      return;
    }
    if (pending.epochAtSchedule !== readSuppressEpoch()) {
      pendingRef.current = null;
      return;
    }
    if (
      pending.discardGeneration != null &&
      pending.discardGeneration !== readDiscardGeneration()
    ) {
      pendingRef.current = null;
      return;
    }
    pendingRef.current = null;
    onSaveStateRef.current?.("saving");
    const operation = Promise.resolve(
      onCommitRef.current(pending.index, pending.values)
    )
      .then((saved) => {
        // Draft discarded while in flight (Use Server) — do not resurrect pending.
        if (
          pausedRef.current ||
          pending.epochAtSchedule !== readSuppressEpoch() ||
          (pending.discardGeneration != null &&
            pending.discardGeneration !== readDiscardGeneration())
        ) {
          pendingRef.current = null;
          return saved;
        }
        if (!saved) throw new Error("calculation_save_failed");
        // Prefer parent merge fingerprint (new item ids); fall back to what we sent.
        lastSentHashRef.current =
          settleFingerprintRef?.current || pending.hash;
        onSaveStateRef.current?.("saved");
        return saved;
      })
      .catch((error) => {
        // Never re-queue a discarded local delete after conflict / Use Server.
        if (
          pausedRef.current ||
          pending.epochAtSchedule !== readSuppressEpoch() ||
          (pending.discardGeneration != null &&
            pending.discardGeneration !== readDiscardGeneration())
        ) {
          pendingRef.current = null;
          return;
        }
        pendingRef.current = pendingRef.current || pending;
        onSaveStateRef.current?.("error");
        throw error;
      });
    inFlightRef.current = operation;
    try {
      await operation;
    } finally {
      inFlightRef.current = null;
    }
    if (pendingRef.current) await flushRef.current();
  };
  useEffect(
    () =>
      registerOfferSave(offerId, {
        flush: () => flushRef.current(),
        isDirty: () =>
          !pausedRef.current &&
          Boolean(pendingRef.current || inFlightRef.current),
      }),
    [offerId]
  );
  // Stable callback refs — parent re-render must not cancel the debounce
  // (onLocalSync updates parent state every keystroke / DnD).
  const onCommitRef = useRef(onCommit);
  const onLocalSyncRef = useRef(onLocalSync);
  onCommitRef.current = onCommit;
  onLocalSyncRef.current = onLocalSync;

  // Conflict banner: stay mounted but drop pending so unmount/flushOfferSaves
  // cannot PUT the discarded local delete after «Bruk server».
  const prevPausedRef = useRef(paused);
  useEffect(() => {
    if (prevPausedRef.current === paused) return;
    prevPausedRef.current = paused;
    if (paused) {
      discardPending();
      onSaveStateRef.current?.("saved");
    }
  }, [paused]);

  // Cancel queued Autosave when parent bumps suppress (placement PUT already
  // has the patched items). Do NOT rewrite lastHash to the current Formik
  // snapshot — that marked unsaved field edits as "already saved" and they
  // never reached the API after the discarded pending write.
  const prevSuppressRef = useRef(suppressEpoch);
  useEffect(() => {
    if (prevSuppressRef.current === suppressEpoch) return;
    prevSuppressRef.current = suppressEpoch;
    discardPending();
  }, [suppressEpoch]);

  // After any successful persist (Autosave or placement), mark the merged
  // fingerprint as already saved. Do not clear a pending newer edit.
  const prevSettleRef = useRef(settleEpoch);
  useEffect(() => {
    if (prevSettleRef.current === settleEpoch) return;
    prevSettleRef.current = settleEpoch;
    const savedHash =
      settleFingerprintRef?.current ||
      calculationPersistFingerprint(valuesRef.current);
    lastSentHashRef.current = savedHash;
    // Keep the last observed draft hash until Formik adopts the saved values.
    // Replacing it here makes a lagging pre-save render look like a new edit
    // and repeatedly recreates rows whose server IDs have not arrived yet.
  }, [settleEpoch, settleFingerprintRef]);

  useEffect(() => {
    if (paused) return;
    const hash = calculationPersistFingerprint(values);
    if (isInitialRef.current) {
      isInitialRef.current = false;
      // Prefer parent settle fingerprint when present (Use Server remount) so a
      // lagging Formik draft cannot become the baseline and onLocalSync-delete.
      const settled = settleFingerprintRef?.current;
      if (settled) {
        lastHashRef.current = settled;
        lastSentHashRef.current = settled;
        if (hash === settled) return;
        // Values still stale — wait for Formik reinit / remount; do not sync.
        return;
      }
      lastHashRef.current = hash;
      lastSentHashRef.current = hash;
      return;
    }
    // Same persistable content (re-render / identity churn) — keep debounce.
    if (hash === lastHashRef.current) return;
    // Server/Formik echo of content we already persisted — do not PUT again.
    // A remote snapshot updates the parent-owned ref synchronously, before
    // React necessarily renders its settleEpoch. Treat that acknowledged
    // content as saved even during the intermediate Formik/grid render.
    if (hash === lastSentHashRef.current || hash === settleFingerprintRef?.current) {
      lastHashRef.current = hash;
      lastSentHashRef.current = hash;
      return;
    }
    lastHashRef.current = hash;
    // Block soft-sync immediately — do not wait for the debounced PUT.
    onLocalSyncRef.current?.(selectedIndex, values);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const epochAtSchedule = readSuppressEpoch();
    pendingRef.current = {
      index: selectedIndex,
      values,
      hash,
      epochAtSchedule,
      discardGeneration: readDiscardGeneration(),
    };
    onSaveStateRef.current?.("pending");
    timeoutRef.current = setTimeout(() => {
      flushRef.current().catch(() => {});
    }, 900);
    // Intentionally no effect-cleanup clear: onLocalSync updates parent and can
    // change the values object identity without content change; clearing on
    // every identity churn would cancel the PUT forever.
  }, [values, selectedIndex, settleFingerprintRef, paused]);

  return null;
}
